import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectState as toggleArrayValue } from 'helpers/filterHelper';
import { apiCallStatus } from 'helpers/constants';

import { checkDownloadLogsLimit, downloadLogsZip, resetDownloadLogsState } from '../actions';
import { getDownloadLogsLimitCheck, getDownloadLogsZip } from '../selectors';
import { getInitialDownloadLogsFormState, isDownloadLogsFormValid, buildDownloadLogsPayload, buildDownloadLogsFileName, isFileCountLimitExceeded } from './helpers';
import { DownloadLogsModalStatus } from './constants';

/**
 * Custom hook driving the Download logs modal local state machine.
 * FORM -> CHECKING -> READY | EXCEEDED -> PREPARING -> (closes on success) | TIMEOUT
 * Any field change made after a check/download attempt resets the flow back to FORM.
 * The check-limit/download-zip API calls themselves are dispatched to redux-saga
 * (see GatewayEventsSaga); this hook only derives the local UI status from their result.
 *
 * @param {boolean} show - whether the modal is currently visible
 * @param {string} tabType - active tab (SMART_SENSOR_DCU / SMART_SENSOR constant value)
 * @param {Function} onClose - callback invoked to close the modal (also called on download success)
 */
export const useDownloadLogsModal = (show, tabType, onClose) => {
    const dispatch = useDispatch();
    const limitCheck = useSelector(getDownloadLogsLimitCheck);
    const zipDownload = useSelector(getDownloadLogsZip);

    const [formState, setFormState] = useState(getInitialDownloadLogsFormState);
    const [status, setStatus] = useState(DownloadLogsModalStatus.FORM);

    useEffect(() => {
        if (show) {
            setFormState(getInitialDownloadLogsFormState());
            setStatus(DownloadLogsModalStatus.FORM);
            dispatch(resetDownloadLogsState());
        }
    }, [show, tabType, dispatch]);

    // Derive the CHECKING/READY/EXCEEDED status from the check-download-limit request. A failed
    // check surfaces via a notify() toast from the saga and simply returns to FORM (the TIMEOUT
    // status/retry UI is reserved for the download-zip request failing, not this one).
    useEffect(() => {
        if (limitCheck?.loadingStatus === apiCallStatus.LOADING) {
            setStatus(DownloadLogsModalStatus.CHECKING);
        } else if (limitCheck?.loadingStatus === apiCallStatus.SUCCESS) {
            const isWithinLimits = limitCheck?.data?.isDownLoadLimitExceededInBytes && !isFileCountLimitExceeded(limitCheck?.data);
            setStatus(isWithinLimits ? DownloadLogsModalStatus.READY : DownloadLogsModalStatus.EXCEEDED);
        } else if (limitCheck?.loadingStatus === apiCallStatus.ERROR) {
            setStatus(DownloadLogsModalStatus.FORM);
        }
    }, [limitCheck?.loadingStatus, limitCheck?.data]);

    const previousZipLoadingStatusRef = useRef(zipDownload?.loadingStatus);
    useEffect(() => {
        const previousLoadingStatus = previousZipLoadingStatusRef.current;
        previousZipLoadingStatusRef.current = zipDownload?.loadingStatus;

        if (zipDownload?.loadingStatus === apiCallStatus.LOADING) {
            setStatus(DownloadLogsModalStatus.PREPARING);
        } else if (zipDownload?.loadingStatus === apiCallStatus.SUCCESS && previousLoadingStatus === apiCallStatus.LOADING) {
            onClose();
        } else if (zipDownload?.loadingStatus === apiCallStatus.ERROR) {
            setStatus(DownloadLogsModalStatus.TIMEOUT);
        }
    }, [zipDownload?.loadingStatus]);

    const updateField = useCallback((field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
        setStatus(DownloadLogsModalStatus.FORM);
        dispatch(resetDownloadLogsState());
    }, [dispatch]);

    const toggleField = useCallback((field, value) => {
        setFormState((prev) => ({ ...prev, [field]: toggleArrayValue(prev[field], value) }));
        setStatus(DownloadLogsModalStatus.FORM);
        dispatch(resetDownloadLogsState());
    }, [dispatch]);

    const handleSelectDate = useCallback((from, to) => {
        updateField('dateSelection', { from, to });
    }, [updateField]);

    const handleCheckDownloadLimit = useCallback(() => {
        if (!isDownloadLogsFormValid(formState)) {
            return;
        }
        const payload = buildDownloadLogsPayload(tabType, formState);
        dispatch(checkDownloadLogsLimit(payload));
    }, [formState, tabType, dispatch]);

    const runDownload = useCallback(() => {
        const payload = { fileIds: limitCheck?.data?.fileIds, downloadEstimateToken: '' };
        dispatch(downloadLogsZip(payload, buildDownloadLogsFileName()));
    }, [dispatch, limitCheck]);

    return {
        formState,
        status,
        checkResult: limitCheck?.data || null,
        updateField,
        toggleField,
        handleSelectDate,
        handleCheckDownloadLimit,
        handleDownload: runDownload,
        handleRetryDownload: runDownload,
        isFormValid: isDownloadLogsFormValid(formState)
    };
};
